import React, { useState } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  CircularProgress,
  Typography,
  Modal,
  Paper,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Store } from '../../../types';
import { useStoreSelector } from './useStoreSelector';

type StoreSelectorProps = {
  label: string;
  placeholder: string;
  multiple?: boolean;
  value?: Store | Store[] | null;
  onChange: (value: Store | Store[] | null) => void;
  disabled?: boolean;
  testId?: string;
}

const StoreSelector: React.FC<StoreSelectorProps> = ({
  label,
  placeholder,
  multiple = false,
  value = multiple ? [] : null,
  onChange,
  disabled = false,
  testId,
}) => {
  const [showRemainingStores, setShowRemainingStores] = useState(false);
  
  const {
    inputValue, 
    setInputValue, 
    filteredOptions, 
    handleKeyDown, 
    handlePaste,
    handleChange,
    isLoading,
    error
  } = useStoreSelector(multiple, value, onChange);

  return (
    <Box>
      <Autocomplete
        multiple={multiple}
        data-testid={ testId ?? 'storeSelector' }
        freeSolo
        options={filteredOptions}
        getOptionLabel={opt => String(opt.storeNumber || opt)}
        value={value as any /* cast to let freeSolo work */}
        onChange={(_, newVal) => handleChange(newVal as (Store|string)[])}
        inputValue={inputValue}
        onInputChange={(_, newInput) => setInputValue(newInput)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        isOptionEqualToValue={(opt, val) =>
          (opt as Store).id !== undefined
            ? (opt as Store).id === (val as Store).id
            : String(opt) === String(val)
        }
        renderTags={(selected, getTagProps) => {
          const maxVisible = 3;
          const visible = (selected as Store[]).slice(0, maxVisible);
          const remaining = (selected as Store[]).length - maxVisible;
          
          return (
            <>
              {visible.map((option, idx) => (
                <Chip
                  label={option.storeNumber}
                  {...getTagProps({ index: idx })}
                />
              ))}
              {remaining > 0 && (
                <Chip
                  label={`+${remaining} more`}
                  size="small"
                  sx={{ ml: 0.5, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowRemainingStores(true);
                  }}
                />
              )}
            </>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            variant="outlined"
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading && <CircularProgress size={20} color="inherit" />}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        loading={isLoading}
        noOptionsText="No stores found"
        disabled={disabled}
      />
      {error && (
        <Typography color="error" variant="caption">
          {error}
        </Typography>
      )}
      
      {/* Modal to display remaining store numbers */}
      <Modal
        open={showRemainingStores}
        onClose={() => setShowRemainingStores(false)}
        aria-labelledby="remaining-stores-modal"
        data-testid={testId ? `${testId}-modal` : 'storeSelector-modal'}
      >
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            maxHeight: '80vh',
            p: 4,
            outline: 'none',
            overflow: 'auto',
          }}
          data-testid={testId ? `${testId}-modal-content` : 'storeSelector-modal-content'}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="h2">
              All Selected Stores
            </Typography>
            <IconButton 
              onClick={() => setShowRemainingStores(false)}
              size="small"
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
            {Array.isArray(value) && value.map((store, index) => (
              <Chip
                key={index}
                label={`${store.storeNumber}`}
                onDelete={() => {
                  if (Array.isArray(value)) {
                    const newValue = value.filter((_, i) => i !== index);
                    onChange(newValue.length > 0 ? newValue : (multiple ? [] : null));
                  }
                }}
                sx={{ m: 0.5 }}
              />
            ))}
          </Box>
        </Paper>
      </Modal>
    </Box>
  );
};

export default StoreSelector;
